import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  ImageOff,
  Info,
  Maximize2,
  MessageSquare,
  RefreshCw,
  Repeat,
  Terminal,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react';

import { useBlobImageUrl } from '../../hooks/useBlobImageUrl';
import { reviewSelfieExceptionErrorMessage } from '../../api/selfieVerificationExceptions';
import type {
  EmployeeEnrollment,
  SelfieVerificationExceptionEmployee,
  SelfieVerificationExceptionItem,
} from '../../interfaces/SelfieEnrollment';
import { EnmSpin, EnrollmentChip } from '../../components/enrollment/EnrollmentChip';
import { enrollmentReason, fmtWhen } from '../../util/enrollmentReasons';
import {
  AZURE_STATUS_LABELS,
  Avatar,
  confPct,
  confTone,
  employeeName,
  formatDateTimeMono,
  formatDayMono,
  formatGap,
  isPending,
  ordinalLabel,
  reasonMeta,
} from './SelfieExceptionsPage';

type PhotoKind = 'selfie' | 'enrollment';

/**
 * TAS-568: a Not-enrolled exception forks on the employee's CURRENT enrollment
 * state. `null` = not this reason, or truly no photo — today's violet panel.
 */
type EnrollmentFork = 'failed' | 'verifying' | 'enrolled' | null;

function enrollmentFork(
  row: SelfieVerificationExceptionItem,
  enrollment: EmployeeEnrollment | undefined
): EnrollmentFork {
  if (row.exceptionReason !== 'employee_not_enrolled') return null;
  const s = enrollment?.state;
  return s === 'failed' || s === 'verifying' || s === 'enrolled' ? s : null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Photo stage — loading / loaded / failed+retry / absent, all required states.
   These are authenticated fetches turned into blob object URLs, so "loading"
   is real and lasts.
   ═══════════════════════════════════════════════════════════════════════════ */

function PhotoStage({
  kind,
  url,
  row,
  enrollment,
  fork = null,
  onZoom,
  onOpenEnrollment,
}: {
  kind: PhotoKind;
  url: string | null;
  row: SelfieVerificationExceptionItem;
  enrollment?: EmployeeEnrollment;
  fork?: EnrollmentFork;
  onZoom: (kind: PhotoKind, blobUrl: string) => void;
  onOpenEnrollment: () => void;
}) {
  // useBlobImageUrl keys its effect on the path string, so re-firing a failed
  // request needs the path to change. A nonce param the API ignores does that
  // without colliding with ?size=thumb composition.
  const [retryNonce, setRetryNonce] = useState(0);
  const fetchUrl = useMemo(() => {
    if (!url) return null;
    if (retryNonce === 0) return url;
    return `${url}${url.includes('?') ? '&' : '?'}_r=${retryNonce}`;
  }, [url, retryNonce]);

  const { blobUrl, loading, error } = useBlobImageUrl(fetchUrl);

  const isSelfie = kind === 'selfie';
  const absent = !url;
  const failed = !absent && !loading && (!!error || !blobUrl);

  const stageClass = absent ? 'absent' : loading ? 'loading' : failed ? 'failed' : '';
  // The enrollment overlay (failed / verifying) sits on the photo; with no photo the
  // absent panel stays the finding.
  const overlayFork = !isSelfie && !absent && (fork === 'failed' || fork === 'verifying') ? fork : null;
  const n = enrollment;

  return (
    <div className={`pcard ${isSelfie ? 'selfie' : 'enroll'}`}>
      <div className="pc-lab">
        {isSelfie ? <Camera /> : <UserCheck />}
        <span className="t">{isSelfie ? 'Punch selfie' : 'Current enrollment photo'}</span>
      </div>

      <div className={`pc-stage ${stageClass}${overlayFork === 'failed' ? ' enfail' : ''}`}>
        {overlayFork === 'failed' && n && (
          <div className="enf-over">
            <div className="t">
              <AlertTriangle />
              Enrollment failed
            </div>
            <div className="row">
              <EnrollmentChip enrollment={n} />
            </div>
            <div className="s">
              Uploaded {fmtWhen(n.queuedAt ?? n.enrolledAt)} · rejected {fmtWhen(n.failedAt)}. Clock-in can&rsquo;t
              verify {row.employee.firstName} until this is fixed.
            </div>
            <div className="row">
              <button type="button" className="btn sm" onClick={onOpenEnrollment}>
                <ArrowRight />
                Fix enrollment
              </button>
            </div>
          </div>
        )}
        {overlayFork === 'verifying' && n && (
          <div className="enf-over ver">
            <div className="t">
              <EnmSpin />
              Verifying with Azure
            </div>
            <div className="s">Uploaded {fmtWhen(n.queuedAt ?? n.enrolledAt)} · usually within 15 minutes.</div>
          </div>
        )}
        {!absent && !loading && !failed && blobUrl && (
          <>
            <div className="shot">
              <img
                src={blobUrl}
                alt={isSelfie ? `Punch selfie for ${employeeName(row)}` : `Enrollment photo for ${employeeName(row)}`}
              />
            </div>
            <button type="button" className="zoom" onClick={() => onZoom(kind, blobUrl)}>
              <Maximize2 />
              Enlarge
            </button>
          </>
        )}

        {failed && (
          <div className="pc-msg">
            <ImageOff />
            <h4>{isSelfie ? "Couldn't load this selfie" : "Couldn't load the enrollment photo"}</h4>
            <p>
              {isSelfie
                ? 'The image request failed. The blob may have been purged by retention.'
                : 'The image request failed. Try again, or open the employee record.'}
            </p>
            <button type="button" className="btn sm" onClick={() => setRetryNonce((n) => n + 1)}>
              <RefreshCw />
              Try again
            </button>
          </div>
        )}

        {/* Not an error — for employee_not_enrolled this IS the finding. */}
        {absent && (
          <div className="pc-msg">
            <UserPlus />
            <h4>No enrollment photo on file</h4>
            <p>
              {row.employee.firstName} has never been enrolled, so there is nothing to compare the
              selfie to.
            </p>
            <button type="button" className="btn sm" onClick={onOpenEnrollment}>
              <UserPlus />
              Add enrollment photo
            </button>
          </div>
        )}
      </div>

      {isSelfie ? (
        <div className="pc-cap">
          <Clock />
          {formatDateTimeMono(row.punchedAt)} ·{' '}
          {row.punchType === 'clock_in' ? 'clock in' : 'clock out'}
        </div>
      ) : absent ? (
        <div className="pc-cap">enrollmentImageUrl: null</div>
      ) : overlayFork === 'failed' ? (
        <div className="pc-cap">
          <Info />
          Photo on file, but not enrolled in Azure
        </div>
      ) : (
        // The enrollment photo is joined live from the employee record, not
        // snapshotted at exception time — say so rather than implying otherwise.
        <div className="pc-cap warn">
          <Info />
          <span>
            This is the photo on file <b>now</b> — if it was replaced after{' '}
            {formatDayMono(row.punchedAt)}, it isn&rsquo;t what Azure compared against.
          </span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Lightbox
   ═══════════════════════════════════════════════════════════════════════════ */

function Lightbox({
  kind,
  blobUrl,
  caption,
  onClose,
}: {
  kind: PhotoKind;
  blobUrl: string;
  caption: string;
  onClose: () => void;
}) {
  return (
    <div className="sfx-zoom-bg" onMouseDown={onClose}>
      <div
        className={`zshot${kind === 'enrollment' ? ' sq' : ''}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <img src={blobUrl} alt={caption} />
      </div>
      <div className="zcap">{caption}</div>
      <button type="button" className="zx" onClick={onClose} aria-label="Close">
        <X />
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Verdict band — reason and confidence as one statement, not two of eleven fields
   ═══════════════════════════════════════════════════════════════════════════ */

function VerdictBand({
  row,
  threshold,
  enrollment,
  fork,
  onOpenEnrollment,
}: {
  row: SelfieVerificationExceptionItem;
  threshold: number;
  enrollment?: EmployeeEnrollment;
  fork: EnrollmentFork;
  onOpenEnrollment: () => void;
}) {
  const meta = reasonMeta(row.exceptionReason);
  const { Icon } = meta;
  const conf = row.azureConfidence;
  const thresholdPct = confPct(threshold);
  const first = row.employee.firstName;

  // Not enrolled, but a photo IS on file: say what actually happened (copy table §18).
  // The exception's own chip stays "Not enrolled" — that is what the exception is.
  let say: React.ReactNode = meta.sentence(conf == null ? null : confPct(conf), thresholdPct);
  let action: React.ReactNode = meta.action;
  let forkLink: string | null = null;
  if (fork === 'failed') {
    say = (
      <>
        {first} has an enrollment photo on file, but <b>Azure rejected it</b> (
        {enrollmentReason(enrollment?.reason).label}), so there was nothing to compare this selfie against.
      </>
    );
    action = (
      <>
        <b>Fix the enrollment, not the punch.</b> Every punch raises one of these until it&rsquo;s fixed. Fixing it
        won&rsquo;t close this exception, so mark it reviewed once you&rsquo;ve looked.
      </>
    );
    forkLink = 'Fix enrollment';
  } else if (fork === 'verifying') {
    say = (
      <>
        {first}&rsquo;s enrollment photo is <b>being verified now</b>. When this punch was checked there was no
        working enrollment to compare against.
      </>
    );
    action = (
      <>
        <b>Nothing to fix right now.</b> Mark this reviewed once you&rsquo;ve looked. Punches after verification
        finishes will be compared as normal.
      </>
    );
    forkLink = 'View enrollment';
  } else if (fork === 'enrolled') {
    say = (
      <>
        {first} is <b>enrolled now</b>, but wasn&rsquo;t when this punch was checked, so there was nothing to
        compare this selfie against.
      </>
    );
    action = (
      <>
        <b>Nothing left to fix.</b> Mark this reviewed.
      </>
    );
  }

  return (
    <div className={`verdict ${meta.cls}`}>
      <div className="vd-top">
        <span className={`rchip ${meta.cls}`}>
          <Icon />
          {meta.label}
        </span>
        <div className="vd-score">
          {conf == null ? (
            <>
              <div className="n none">Not scored</div>
              <div className="l">no comparison ran</div>
            </>
          ) : (
            <>
              <div className={`n ${confTone(conf)}`}>{confPct(conf)}%</div>
              <div className="l">Confidence</div>
            </>
          )}
        </div>
      </div>

      {/* No meter at all when nothing was scored — an empty bar reads as a bug. */}
      {conf != null && (
        <div className="vd-meter">
          <div className="meter">
            <div
              className={`fill ${confTone(conf)}`}
              style={{ width: `${Math.min(100, confPct(conf))}%` }}
            />
            <div className="thr" style={{ left: `${thresholdPct}%` }} />
          </div>
          <div className="vd-scale">
            <span>0%</span>
            <span>match threshold {thresholdPct}%</span>
            <span>100%</span>
          </div>
        </div>
      )}

      <div className="vd-say">{say}</div>

      <div className="vd-do">
        <ArrowRight />
        <div>
          {action}
          {row.exceptionReason === 'employee_not_enrolled' && fork === null && (
            <button type="button" className="vd-link" onClick={onOpenEnrollment}>
              <UserPlus />
              Open enrollment photo
            </button>
          )}
          {forkLink && (
            <button type="button" className="vd-link" onClick={onOpenEnrollment}>
              {forkLink}
              <ChevronRight />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Technical block — collapsed by default
   ═══════════════════════════════════════════════════════════════════════════ */

function TechnicalBlock({ row }: { row: SelfieVerificationExceptionItem }) {
  const gap = formatGap(row.punchedAt, row.verifiedAt);
  const azureLabel = AZURE_STATUS_LABELS[row.azureStatus];

  const timeEntryValue = row.timeEntryDeleted
    ? 'deleted after the exception was raised'
    : row.timeEntry?.type === 'hourly'
    ? `timeEntryHourlyID ${row.timeEntry.timeEntryHourlyID}`
    : row.timeEntry?.type === 'job'
    ? `timeEntryID ${row.timeEntry.timeEntryID}`
    : '—';

  return (
    <details className="tech">
      <summary>
        <Terminal />
        Technical detail
        <ChevronDown className="chev" />
      </summary>
      <div className="tgrid">
        <div>
          <div className="lab">Exception ID</div>
          <div className="val">{row.selfieVerificationExceptionID}</div>
        </div>
        <div>
          <div className="lab">Employee ID</div>
          <div className="val">
            {row.employee.altEmployeeID} / {row.employee.employeeID}
          </div>
        </div>
        <div>
          <div className="lab">Azure status</div>
          <div className="val">
            {azureLabel ? `${azureLabel} · ${row.azureStatus}` : row.azureStatus}
          </div>
        </div>
        <div>
          <div className="lab">Raw confidence</div>
          <div className={`val${row.azureConfidence == null ? ' muted' : ''}`}>
            {row.azureConfidence == null ? 'null' : row.azureConfidence.toFixed(4)}
          </div>
        </div>
        <div>
          <div className="lab">Punched at (UTC)</div>
          <div className="val">{row.punchedAt}</div>
        </div>
        <div>
          <div className="lab">Verified at (UTC)</div>
          <div className={`val${row.verifiedAt ? '' : ' muted'}`}>
            {row.verifiedAt ? `${row.verifiedAt}${gap ? ` · ${gap}` : ''}` : 'never scored'}
          </div>
        </div>
        <div>
          <div className="lab">Exception raised (UTC)</div>
          <div className="val">{row.createdAt}</div>
        </div>
        <div>
          <div className="lab">Time entry</div>
          <div className={`val${row.timeEntryDeleted || !row.timeEntry ? ' muted' : ''}`}>
            {timeEntryValue}
          </div>
        </div>
      </div>
    </details>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Pinned review footer
   ═══════════════════════════════════════════════════════════════════════════ */

function ReviewFooter({
  row,
  saving,
  onSubmit,
}: {
  row: SelfieVerificationExceptionItem;
  saving: boolean;
  onSubmit: (id: number, note: string | null) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // The note belongs to one exception; walking the queue must not carry it over.
  useEffect(() => {
    setNote('');
    setError(null);
  }, [row.selfieVerificationExceptionID]);

  if (!isPending(row)) {
    return (
      <div className="sd-review">
        <div className="gr-card">
          <div className="gr-head">
            <span className="rev-pill ok">
              <span className="dot" />
              Reviewed
            </span>
            <span className="gr-who">
              by <b>{row.reviewedBy ?? 'Unknown'}</b>
              {row.reviewedAt ? ` · ${formatDateTimeMono(row.reviewedAt)}` : ''}
            </span>
          </div>
          <p className={`gr-note${row.reviewNote ? '' : ' empty'}`}>
            {row.reviewNote ?? 'No note left.'}
          </p>
        </div>
      </div>
    );
  }

  const submit = async () => {
    setError(null);
    try {
      await onSubmit(row.selfieVerificationExceptionID, note.trim() || null);
      // Success unmounts or advances; the effect above clears the note either way.
    } catch (e) {
      // Keep the typed note — it lives in this component's state.
      setError(reviewSelfieExceptionErrorMessage(e));
    }
  };

  return (
    <div className="sd-review">
      <label className="gr-lab" htmlFor="sfx-detail-note">
        <MessageSquare />
        Note
        <span className="cnt">{note.length} / 1000</span>
      </label>
      <textarea
        id="sfx-detail-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={'Optional — e.g. "Verified with employee by phone, hard hat shadow."'}
        maxLength={1000}
        disabled={saving}
      />
      {error && (
        <div className="gr-err">
          <AlertTriangle />
          {`Couldn't save the review — ${error}`}
        </div>
      )}
      <div className="gr-actions">
        <button type="button" className="btn review-solid" onClick={submit} disabled={saving}>
          {saving ? <RefreshCw className="spin" /> : <Check />}
          {saving ? 'Saving…' : 'Mark reviewed'}
        </button>
        <span className="gr-hint">Reviewing can&rsquo;t be undone — the note is the record.</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Modal
   ═══════════════════════════════════════════════════════════════════════════ */

interface Props {
  row: SelfieVerificationExceptionItem;
  /** Position within the filtered, sorted list. */
  index: number;
  total: number;
  threshold: number;
  repeatOrdinal: number | null;
  repeatTotal: number;
  saving: boolean;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onSubmitReview: (id: number, note: string | null) => Promise<void>;
  /**
   * TAS-568: the employee's newest known enrollment (a change made on the page,
   * else the issues list, else the row). Drives the Not-enrolled fork.
   */
  enrollment?: EmployeeEnrollment;
  /** Opens the shared Enrollment Photo modal (stacked above this detail). */
  onOpenEnrollment: (employee: SelfieVerificationExceptionEmployee) => void;
}

const SelfieExceptionDetailModal: React.FC<Props> = ({
  row,
  index,
  total,
  threshold,
  repeatOrdinal,
  repeatTotal,
  saving,
  onClose,
  onPrev,
  onNext,
  onSubmitReview,
  enrollment,
  onOpenEnrollment,
}) => {
  const [zoom, setZoom] = useState<{ kind: PhotoKind; blobUrl: string } | null>(null);

  const openEnrollment = useCallback(() => onOpenEnrollment(row.employee), [onOpenEnrollment, row]);
  // Re-derives on every render, so a fix made in the stacked photo modal flips the copy at once.
  const fork = enrollmentFork(row, enrollment);

  // Escape closes the lightbox first, leaving the detail open. Arrow keys walk
  // the queue, but not while the reviewer is typing a note.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        !!target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT');

      if (e.key === 'Escape') {
        if (zoom) setZoom(null);
        else onClose();
        return;
      }
      if (typing || zoom) return;
      if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom, onClose, onPrev, onNext]);

  const zoomCaption =
    zoom?.kind === 'selfie'
      ? `Punch selfie · ${formatDateTimeMono(row.punchedAt)}`
      : `Current enrollment photo · ${employeeName(row)}`;

  return (
    <>
      <div className="sfx-detail-bg" onMouseDown={onClose}>
        <div className="sfx-detail" onMouseDown={(e) => e.stopPropagation()}>
          <div className="sd-head">
            <Avatar
              employeeID={row.employee.employeeID}
              firstName={row.employee.firstName}
              lastName={row.employee.lastName}
            />
            <div className="sd-who">
              <div className="nm">
                {employeeName(row)}
                {repeatOrdinal != null && (
                  <span className="repeat">
                    <Repeat />
                    {ordinalLabel(repeatOrdinal)} of {repeatTotal} this week
                  </span>
                )}
              </div>
              <div className="sub">
                <span>{row.employee.altEmployeeID}</span>
                <span>{formatDateTimeMono(row.punchedAt)}</span>
                <span>{row.punchType === 'clock_in' ? 'clock in' : 'clock out'}</span>
              </div>
            </div>
            <div className="sd-nav">
              <span className="pos">
                {index >= 0 ? index + 1 : '—'} of {total}
              </span>
              <button
                type="button"
                className="navb"
                onClick={onPrev}
                disabled={!onPrev}
                aria-label="Previous exception"
              >
                <ChevronLeft />
              </button>
              <button
                type="button"
                className="navb"
                onClick={onNext}
                disabled={!onNext}
                aria-label="Next exception"
              >
                <ChevronRight />
              </button>
            </div>
            <button type="button" className="x" onClick={onClose} aria-label="Close">
              <X />
            </button>
          </div>

          <div className="sd-scroll">
            <VerdictBand
              row={row}
              threshold={threshold}
              enrollment={enrollment}
              fork={fork}
              onOpenEnrollment={openEnrollment}
            />

            <div className="photos">
              <PhotoStage
                kind="selfie"
                url={row.selfieImageUrl}
                row={row}
                onZoom={(kind, blobUrl) => setZoom({ kind, blobUrl })}
                onOpenEnrollment={openEnrollment}
              />
              <PhotoStage
                kind="enrollment"
                url={row.enrollmentImageUrl}
                row={row}
                enrollment={enrollment}
                fork={fork}
                onZoom={(kind, blobUrl) => setZoom({ kind, blobUrl })}
                onOpenEnrollment={openEnrollment}
              />
            </div>

            <TechnicalBlock row={row} />
          </div>

          <ReviewFooter row={row} saving={saving} onSubmit={onSubmitReview} />
        </div>
      </div>

      {zoom && (
        <Lightbox
          kind={zoom.kind}
          blobUrl={zoom.blobUrl}
          caption={zoomCaption}
          onClose={() => setZoom(null)}
        />
      )}
    </>
  );
};

export default SelfieExceptionDetailModal;
